/**
 * Cloud implementation of the shared GeoMapApi. Node-scoped: binds the currently selected
 * node id, like the groups/models/map-layout adapters.
 *
 * Nothing heavy crosses the tunnel here — a camera's position is four numbers, and the
 * basemap is fetched by the operator's own browser from the tile URL the node's config
 * names. That split is why a cloud session can draw the map for a site whose node has no
 * internet of its own.
 */
import type { GeoMapApi, GeoMapConfig, GeoPlacement, GeoPlacementSave } from '@trinetra/shared';
import {
  getNodeGeoMapConfig,
  getNodeGeoPlacements,
  setNodeGeoMapConfig,
  setNodeGeoPlacements,
} from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

/** Node id or '' — the shared page renders an empty state when no node is picked. */
const nid = () => nodeId() || '';

/** Re-throw a proxy failure with the NODE's message; the cloud BE relays it verbatim. */
function rethrow(e: any): never {
  const body = e?.response?.data;
  throw new Error(body?.message || body?.error || e?.message || 'Request failed');
}

export const cloudGeoMapApi: GeoMapApi = {
  async config(): Promise<GeoMapConfig> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return (await getNodeGeoMapConfig(id).catch(rethrow)) as GeoMapConfig;
  },

  async saveConfig(input): Promise<GeoMapConfig> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return (await setNodeGeoMapConfig(id, {
      center_lat: input.center_lat,
      center_lng: input.center_lng,
      zoom: input.zoom,
      ...(input.tile_url !== undefined ? { tile_url: input.tile_url } : {}),
      ...(input.tile_attribution !== undefined ? { tile_attribution: input.tile_attribution } : {}),
      ...(input.geocoder_url !== undefined ? { geocoder_url: input.geocoder_url } : {}),
    }).catch(rethrow)) as GeoMapConfig;
  },

  async placements(): Promise<GeoPlacement[]> {
    const id = nid();
    if (!id) return [];
    const rows = await getNodeGeoPlacements(id).catch(rethrow);
    return Array.isArray(rows) ? rows : [];
  },

  async savePlacements(input: GeoPlacementSave): Promise<GeoPlacement[]> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    const rows = await setNodeGeoPlacements(id, {
      placed: input.placed || [],
      cleared: input.cleared || [],
    }).catch(rethrow);
    return Array.isArray(rows) ? rows : [];
  },
};
